/**
 * sidebarPrefs.ts
 *
 * Per-browser persistence for sidebar collapsed/expanded state.
 *
 * Two independent sidebars share this module:
 *   - Main nav sidebar (shadcn `SidebarProvider`)
 *   - Recent Patients rail (`usePatientHistoryStore.sidebarOpen`)
 *
 * State is stored in `localStorage` so it survives page reload and
 * logout/login on the same browser. All access is wrapped in try/catch
 * so the helper is safe under Safari private mode, disabled storage,
 * or any other access failure — callers always receive the supplied
 * default in that case.
 */

const NAV_KEY = 'nh:sidebar:nav-open';
const RECENTS_KEY = 'nh:sidebar:recents-open';

function hasStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function read(key: string, defaultValue: boolean): boolean {
  if (!hasStorage()) return defaultValue;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return defaultValue;
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return defaultValue;
  } catch {
    return defaultValue;
  }
}

function write(key: string, value: boolean): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(key, value ? 'true' : 'false');
  } catch {
    // Storage disabled / quota exceeded -- silently ignore; UI still
    // works for the current session.
  }
}

export const sidebarPrefs = {
  readNav(defaultValue: boolean): boolean {
    return read(NAV_KEY, defaultValue);
  },
  writeNav(open: boolean): void {
    write(NAV_KEY, open);
  },
  readRecents(defaultValue: boolean): boolean {
    return read(RECENTS_KEY, defaultValue);
  },
  writeRecents(open: boolean): void {
    write(RECENTS_KEY, open);
  },
};
